/**
 * Breadcrumb — navigational trail above a PageHeader.
 *
 * Items render as plain text, a clickable link (`onClick`), or a dropdown
 * trigger (`dropdown`). Separators between items are chevron-right icons.
 * Sized for comfortable touch targets; the trailing item is emphasized.
 */

import { Fragment, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";
import { DropdownMenu } from "./Overlay";
import { TapTarget } from "./TapTarget";

export interface BreadcrumbItem {
  label: ReactNode;
  /** Click handler — renders item as a button. Ignored when `dropdown` is set. */
  onClick?: () => void;
  /** Dropdown menu contents. When set, item renders with a chevron-down. */
  dropdown?: ReactNode;
  /** Optional leading icon. */
  icon?: IconName;
  /** Hide this item (and its preceding separator) on screens narrower than `sm`. */
  hideOnMobile?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        "-ml-2.5 flex flex-wrap items-center gap-x-0.5 gap-y-1 text-base text-ink-600",
        className,
      )}
    >
      {items.map((item, i) => {
        const isFirst = i === 0;
        const isLast = i === items.length - 1;
        const key = `${i}-${typeof item.label === "string" ? item.label : ""}`;
        const responsiveCrumb = item.hideOnMobile ? "hidden sm:inline-flex" : "";
        const responsiveSep = item.hideOnMobile ? "hidden sm:inline-block" : "";
        return (
          <Fragment key={key}>
            {!isFirst && (
              <Icon
                name="chevron-right"
                size="sm"
                className={cn("mx-0.5 shrink-0 text-ink-400", responsiveSep)}
              />
            )}
            <BreadcrumbNode item={item} isLast={isLast} responsiveClass={responsiveCrumb} />
          </Fragment>
        );
      })}
    </nav>
  );
}

function BreadcrumbNode({
  item,
  isLast,
  responsiveClass,
}: {
  item: BreadcrumbItem;
  isLast: boolean;
  responsiveClass: string;
}) {
  const label = (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {item.icon && <Icon name={item.icon} size="sm" className="shrink-0 text-ink-500" />}
      <span className="truncate">{item.label}</span>
    </span>
  );

  const baseClass = cn(
    "inline-flex max-w-[10rem] items-center gap-1 rounded-md px-2.5 py-2 transition-colors sm:max-w-xs",
    isLast ? "font-semibold text-ink-900" : "text-ink-600",
    responsiveClass,
  );

  if (item.dropdown) {
    return (
      <DropdownMenu
        align="start"
        trigger={
          <TapTarget className={cn(baseClass, "hover:bg-ink-100")}>
            {label}
            <Icon
              name="chevron-down"
              size="sm"
              className={isLast ? "text-ink-500" : "text-ink-400"}
            />
          </TapTarget>
        }
      >
        {item.dropdown}
      </DropdownMenu>
    );
  }

  if (item.onClick && !isLast) {
    return (
      <TapTarget
        onClick={item.onClick}
        className={cn(baseClass, "hover:bg-ink-100 hover:text-ink-900")}
      >
        {label}
      </TapTarget>
    );
  }

  return <span className={baseClass}>{label}</span>;
}
